import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Camera, ArrowClockwise, Check, X } from '@phosphor-icons/react';

/**
 * SIM PROFILE AVATAR COMPONENT
 * Universal avatar system with custom image upload
 * 
 * Features:
 * - Default cybernetic head for all SIMs
 * - User upload with preview
 * - LocalStorage persistence
 * - Reset to default option
 * - File validation (5MB, images only)
 */

const DEFAULT_AVATAR = 'https://customer-assets.emergentagent.com/wingman/f17fd939-96fd-4a1b-b26f-824c11717462/attachments/de4bdb1143a147e2be29929ba4b6f431_IMG_2823.jpeg';

const SimProfileAvatar = ({
  simId,
  size = 'medium', // 'small', 'medium', 'large'
  showUploadButton = true,
  className = '',
  onImageChange
}) => {
  const [customImage, setCustomImage] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [error, setError] = useState(null);
  const [showHover, setShowHover] = useState(false);
  const fileInputRef = useRef(null);

  const storageKey = `sim_avatar_${simId}`;

  // Size configurations
  const sizeClasses = {
    small: 'w-16 h-16',
    medium: 'w-24 h-24',
    large: 'w-40 h-40'
  };

  const buttonSizes = {
    small: 'w-5 h-5',
    medium: 'w-7 h-7',
    large: 'w-10 h-10'
  };

  // Load custom image from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      setCustomImage(saved);
    }
  }, [storageKey]);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file (jpg, png, webp)');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be smaller than 5MB');
      return;
    }

    setError(null);
    setIsUploading(true);

    // Read file and create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewImage(e.target.result);
      setIsUploading(false);
    };
    reader.onerror = () => {
      setError('Failed to read image');
      setIsUploading(false);
    };
    reader.readAsDataURL(file);
  };

  const handleConfirmUpload = () => {
    if (previewImage) {
      localStorage.setItem(storageKey, previewImage);
      setCustomImage(previewImage);
      setPreviewImage(null);
      if (onImageChange) {
        onImageChange(previewImage);
      }
    }
  };

  const handleCancelUpload = () => {
    setPreviewImage(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleReset = () => {
    localStorage.removeItem(storageKey);
    setCustomImage(null);
    setPreviewImage(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (onImageChange) {
      onImageChange(null);
    }
  };

  const currentImage = customImage || DEFAULT_AVATAR;
  const isCustom = !!customImage;

  return (
    <div className={`relative ${className}`}>
      {/* Avatar Display */}
      <motion.div
        className={`relative ${sizeClasses[size]} rounded-full overflow-hidden group`}
        onHoverStart={() => setShowHover(true)}
        onHoverEnd={() => setShowHover(false)}
        whileHover={{ scale: 1.05 }}
        transition={{ duration: 0.2 }}
      >
        {/* Avatar Image */}
        <img
          src={previewImage || currentImage}
          alt="SIM Profile Avatar"
          className={`w-full h-full object-cover ${
            isCustom 
              ? 'border-2 border-[#00F0FF] ring-2 ring-[#00F0FF]/30' 
              : 'border-2 border-gray-600'
          }`}
          style={{
            filter: isCustom ? 'drop-shadow(0 0 10px rgba(0, 240, 255, 0.5))' : 'none'
          }}
        />

        {/* Overlay on Hover */}
        <AnimatePresence>
          {showUploadButton && showHover && !previewImage && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center"
            >
              <button
                onClick={() => fileInputRef.current?.click()}
                className="p-2 bg-[#00F0FF]/20 border border-[#00F0FF]/50 rounded-full hover:bg-[#00F0FF]/30 transition-all"
              >
                <Camera size={size === 'large' ? 24 : 20} weight="fill" className="text-[#00F0FF]" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Custom Image Indicator */}
        {isCustom && !previewImage && (
          <div className="absolute top-0 right-0 w-5 h-5 bg-[#00F0FF] rounded-full flex items-center justify-center border-2 border-black">
            <Check size={12} weight="bold" className="text-black" />
          </div>
        )}
      </motion.div>

      {/* Upload Button (Below Avatar) */}
      {showUploadButton && !previewImage && (
        <div className="flex gap-2 mt-2 justify-center">
          <button
            onClick={() => fileInputRef.current?.click()}
            className="px-3 py-1.5 bg-[#00F0FF]/20 border border-[#00F0FF]/50 rounded-lg text-xs text-[#00F0FF] font-bold hover:bg-[#00F0FF]/30 transition-all flex items-center gap-2"
          >
            <Camera size={14} weight="fill" />
            {isCustom ? 'Change Face' : 'Add Face'}
          </button>
          
          {isCustom && (
            <button
              onClick={handleReset}
              className="px-3 py-1.5 bg-gray-700/50 border border-gray-600 rounded-lg text-xs text-gray-300 font-bold hover:bg-gray-700 transition-all flex items-center gap-2"
              title="Reset to Default"
            >
              <ArrowClockwise size={14} weight="bold" />
              Reset
            </button>
          )}
        </div>
      )}

      {/* Preview Confirmation */}
      <AnimatePresence>
        {previewImage && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 p-3 bg-black/90 backdrop-blur-md border border-[#00F0FF]/50 rounded-lg z-50 min-w-[200px]"
          >
            <p className="text-xs text-white mb-3 text-center">Confirm new avatar?</p>
            <div className="flex gap-2 justify-center">
              <button
                onClick={handleConfirmUpload}
                className="px-4 py-2 bg-[#00FFA3] text-black font-bold text-xs rounded-lg hover:bg-[#00CC82] transition-all flex items-center gap-1"
              >
                <Check size={14} weight="bold" />
                Confirm
              </button>
              <button
                onClick={handleCancelUpload}
                className="px-4 py-2 bg-red-500/80 text-white font-bold text-xs rounded-lg hover:bg-red-600 transition-all flex items-center gap-1"
              >
                <X size={14} weight="bold" />
                Cancel
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error Message */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 p-2 bg-red-500/90 border border-red-400 rounded-lg text-xs text-white text-center z-50 min-w-[200px]"
          >
            {error}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Loading Indicator */}
      {isUploading && (
        <div className="absolute inset-0 bg-black/70 rounded-full flex items-center justify-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            className="w-8 h-8 border-2 border-[#00F0FF] border-t-transparent rounded-full"
          />
        </div>
      )}
    </div>
  );
};

export default SimProfileAvatar;
